import { useLocale } from 'core/hooks/useLocale';
import { SCORE_STATUS_LABEL_KEYS } from 'core/modules/atoms/Score/useScore';
import { getOriginalScale, isScaleNumeric, isScaleQualitative } from 'core/utils/constants/evaluation';
import { formatDisplayScore } from 'core/utils/formatDisplayScore';
import { parseGradesToScoreScale } from 'core/utils/parses';
import { getUserNoteStatus } from 'core/utils/user';
import PropTypes from 'prop-types';
import React from 'react';
import { withTranslation } from 'react-i18next';
import { Icon } from 'ui';

const AssessmentGradeTag = ({ grade, gradeTotal, isPending, isTimedOut, t, scoreScale, evaluationScale }) => {
  const locale = useLocale();
  const ICONS = {
    'needs-reinforcement': { icon: 'emoji-panic' },
    'needs-ampliacion': { icon: 'emoji-thumbs-up' },
    'good-progress': { icon: 'emoji-master' },
    'timed-out': { icon: 'emoji-panic' },
  };

  const status = isTimedOut ? 'timed-out' : isPending ? 'pending' : getUserNoteStatus(grade);
  // getUserNoteStatus only maps the literal '-' to 'empty', so a null grade would fall into
  // 'needs-reinforcement' and assert a failure that never happened. Guarded like useScore.ts:83.
  const hasGrade = Boolean(grade) || grade === 0;
  const isGradeMissing = !hasGrade && !isPending && !isTimedOut;
  // Status keys shared with the Score atom so both announce the same wording.
  const statusLabelKey = SCORE_STATUS_LABEL_KEYS[isGradeMissing ? 'empty' : status];
  // ICONS has no entry for 'empty', the status of a '-' grade.
  const statusIcon = ICONS[status]?.icon;
  return (
    <div className={`assessment-grade-tag assessment-grade-tag--${status}`}>
      <div className="assessment-grade-tag__grade-container">
        {isTimedOut ? (
          <span className={`assessment-grade-tag__grade`}>{t('Timed out')}</span>
        ) : isPending ? (
          <span className={`assessment-grade-tag__grade`}>{t('lemonade:Waiting correction')}</span>
        ) : (
          <>
            {isScaleNumeric(evaluationScale) ? (
              <>
                <span className={`assessment-grade-tag__grade`}>
                  {formatDisplayScore(getOriginalScale(grade, evaluationScale.min, evaluationScale.max, evaluationScale.number_decimals), locale)}
                </span>
                <span className="assessment-grade-tag__grade-total">/{formatDisplayScore(evaluationScale.max, locale)}</span>
              </>
            ) : isScaleQualitative(evaluationScale) ? (
              <>
                {evaluationScale?.ranges && grade !== null && grade !== undefined ? (
                  <span className={`assessment-grade-tag__grade`}>
                    {evaluationScale.ranges.find((range) => {
                      const gradeValue = grade * 10;
                      // Si from es 100, permitimos que gradeValue sea igual a from
                      if (range.from === 100) {
                        return gradeValue >= range.to && gradeValue <= range.from;
                      } else {
                        return gradeValue >= range.to && gradeValue < range.from;
                      }
                    })?.name || '-'}
                  </span>
                ) : (
                  <span className={`assessment-grade-tag__grade`}>-</span>
                )}
              </>
            ) : (
              <>
                <span className={`assessment-grade-tag__grade`}>{formatDisplayScore(parseGradesToScoreScale(grade, scoreScale) || 0, locale)}</span>
                <span className="assessment-grade-tag__grade-total">/{formatDisplayScore(parseGradesToScoreScale(gradeTotal, scoreScale), locale)}</span>
              </>
            )}
          </>
        )}
      </div>
      {!isPending && statusIcon && (
        <div className="assessment-grade-tag__icon">
          <Icon name={statusIcon} />
        </div>
      )}
      {statusLabelKey && <span className="sr-only">{`. ${t(statusLabelKey)}.`}</span>}
    </div>
  );
};

AssessmentGradeTag.propTypes = {
  grade: PropTypes.string,
  gradeTotal: PropTypes.string,
  isTimedOut: PropTypes.bool,
  isPending: PropTypes.bool,
  t: PropTypes.func,
  scoreScale: PropTypes.string,
  evaluationScale: PropTypes.object,
};

AssessmentGradeTag.defaultProps = {
  grade: '1',
  gradeTotal: '10',
  isTimedOut: false,
};

export default withTranslation(['assigments', 'visor', 'lemonade', 'grades'])(AssessmentGradeTag);
